import { ChangeCategory } from '@src/services/ReleaseNotesService';
import { formatDate } from '@src/utility/dateFormat';
import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';

import { extractErrorInfo } from '../../utility/error';
import { LogManager } from '../../utility/Logger';

import type { ProcessedRelease } from '@src/services/ReleaseNotesService';

const logger = LogManager.getLogger('component:ReleaseCard');

interface ReleaseCardProps {
  release: ProcessedRelease;
  hideDate?: boolean;
  hideTitle?: boolean;
}

const CATEGORY_COLORS: Record<ChangeCategory, string> = {
  [ChangeCategory.Feature]: '#17c964',
  [ChangeCategory.Improvement]: '#2196f3',
  [ChangeCategory.BugFix]: '#f2d093',
  [ChangeCategory.Other]: 'var(--text-muted)',
};

const ReleaseCard: React.FC<ReleaseCardProps> = ({
  release,
  hideDate,
  hideTitle,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleThumbnailClick = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    const onFullscreenChange = () => {
      if (!document.fullscreenElement) {
        video.pause();
        document.removeEventListener('fullscreenchange', onFullscreenChange);
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);

    try {
      if (video.requestFullscreen) {
        await video.requestFullscreen();
      } else {
        const safariVideo = video as HTMLVideoElement & {
          webkitEnterFullscreen?: () => void;
        };
        safariVideo.webkitEnterFullscreen?.();
      }
    } catch (error) {
      logger.error('Failed to request fullscreen:', extractErrorInfo(error));
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      return;
    }

    try {
      await video.play();
    } catch (error) {
      logger.error('Failed to play video:', extractErrorInfo(error));
    }
  }, []);

  return (
    <Card>
      {!hideDate && (
        <Header>
          <ReleaseDate>
            {formatDate(release.publishedAt, { month: 'long' }) ??
              release.publishedAt}
          </ReleaseDate>
        </Header>
      )}

      {!hideTitle && <Title>{release.name}</Title>}

      {/* Video thumbnail */}
      {release.video?.videoFilePrimary?.video?.muxPlaybackId && (
        <>
          <ThumbnailContainer>
            <ThumbnailWrapper onClick={handleThumbnailClick}>
              <Thumbnail
                src={release.video.videoFilePrimary.video.thumbnailUrl}
                alt={release.name}
                loading="lazy"
              />
              <PlayButton>
                <PlayIcon viewBox="0 0 24 24" fill="none">
                  <path d="M8 5v14l11-7L8 5z" fill="currentColor" />
                </PlayIcon>
              </PlayButton>
            </ThumbnailWrapper>
            <FullscreenVideo
              ref={videoRef}
              poster={release.video.videoFilePrimary.video.thumbnailUrl}
              controls
              preload="none"
            >
              {release.video.videoFilePrimary.video.mp4Url && (
                <source
                  src={release.video.videoFilePrimary.video.mp4Url}
                  type="video/mp4"
                />
              )}
            </FullscreenVideo>
          </ThumbnailContainer>
          <SectionDivider />
        </>
      )}

      {/* Image */}
      {release.image && (
        <>
          <ImageWrapper>
            <ReleaseImage
              src={release.image.url}
              alt={release.image.title || release.name}
              loading="lazy"
            />
          </ImageWrapper>
          <SectionDivider />
        </>
      )}

      {release.hasCategories ? (
        release.categories.map((category, i) => (
          <CategorySection key={i}>
            <CategoryLabel $color={CATEGORY_COLORS[category.type]}>
              {category.label}
            </CategoryLabel>
            <CategoryContent
              dangerouslySetInnerHTML={{ __html: category.html }}
            />
          </CategorySection>
        ))
      ) : (
        <BodyContent
          dangerouslySetInnerHTML={{ __html: release.sanitizedHtml }}
        />
      )}
    </Card>
  );
};

export default ReleaseCard;

const Card = styled.div`
  padding: var(--spacing-lg) 0;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-subtle);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-xs);
`;

const ReleaseDate = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const Title = styled.h3`
  margin: 0 0 var(--spacing-md) 0;
  font-size: var(--text-md);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
  line-height: var(--leading-heading);
`;

const ThumbnailContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: var(--spacing-md);
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  width: 184px;
  border-radius: var(--radius);
  overflow: hidden;
  cursor: pointer;

  &:hover > div {
    opacity: 1;
  }
`;

const Thumbnail = styled.img`
  width: 100%;
  display: block;
  border-radius: var(--radius);
  background: var(--gray-900);
  aspect-ratio: 16 / 9;
  object-fit: cover;
`;

const PlayButton = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  opacity: 0.8;
  transition: opacity var(--transition-fast) ease;
`;

const PlayIcon = styled.svg`
  width: 32px;
  height: 32px;
  color: white;
`;

const FullscreenVideo = styled.video`
  position: fixed;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;

  &:fullscreen {
    position: static;
    width: 100%;
    height: 100%;
    opacity: 1;
    pointer-events: auto;
    background: black;
  }
`;

const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid var(--border-subtle);
  margin: 0 0 var(--spacing-md) 0;
`;

const ImageWrapper = styled.div`
  margin-bottom: var(--spacing-md);
  border-radius: var(--radius);
  overflow: hidden;
  display: flex;
  justify-content: center;
`;

const ReleaseImage = styled.img`
  width: 62.5%;
  display: block;
  border-radius: var(--radius);
`;

const CategorySection = styled.div`
  margin-bottom: var(--spacing-sm);
`;

const CategoryLabel = styled.div<{ $color: string }>`
  display: inline-block;
  padding: var(--spacing-2xs) var(--spacing-sm);
  border-radius: 4px;
  background: ${p => p.$color}18;
  color: ${p => p.$color};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  margin-bottom: var(--spacing-xs);
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

const contentStyles = `
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: var(--leading-normal);

  ul, ol {
    margin: var(--spacing-xs) 0;
    padding-left: var(--spacing-lg);
  }

  li {
    margin-bottom: var(--spacing-2xs);
  }

  a {
    color: var(--primary);
    text-decoration: none;
    transition: color var(--transition-fast) ease;
    &:hover {
      color: var(--primary-hover);
      text-decoration: underline;
    }
  }

  p {
    margin: var(--spacing-sm) 0;
  }

  strong {
    color: var(--foreground);
    font-weight: var(--font-weight-semi-bold);
  }

  code {
    background: var(--surface);
    padding: 1px var(--spacing-xs);
    border-radius: 3px;
    font-size: 0.9em;
  }
`;

const CategoryContent = styled.div`
  ${contentStyles}
`;

const BodyContent = styled.div`
  ${contentStyles}

  h1, h2, h3 {
    margin: var(--spacing-md) 0 var(--spacing-sm);
    font-weight: var(--font-weight-semi-bold);
    color: var(--foreground);
  }
`;
